import { useEffect, useState } from 'react';
import { User, Phone, Mail, MapPin, Save, Loader2, ShieldCheck, LogIn, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function Profile() {
  const { user, loginWithGoogle, authClient, refreshUser, logout, isPhoneMock } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpRequired, setOtpRequired] = useState(false);
  const [emailConsent, setEmailConsent] = useState(true);
  const [whatsappConsent, setWhatsappConsent] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [addrForm, setAddrForm] = useState({ label: 'Home', address_line: '', phone: '', is_default: false });

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const [{ data: p }, { data: a }] = await Promise.all([
          authClient.get('/profile'),
          authClient.get('/addresses'),
        ]);
        setProfile(p);
        setName(p.name || '');
        const rawPhone = (p.phone || '').replace(/\D/g, '');
        const cleanPhone = rawPhone.length === 12 && rawPhone.startsWith('91') ? rawPhone.slice(2) : rawPhone;
        setPhone(cleanPhone);
        setEmailConsent(p.email_consent);
        setWhatsappConsent(p.whatsapp_consent);
        setAddresses(a);
      } catch {
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authClient]);

  const savedCleanPhone = (profile?.phone || '').replace(/\D/g, '').replace(/^91/, '');
  const phoneChanged = phone !== savedCleanPhone;
  const validPhone = /^[6-9]\d{9}$/.test(phone);

  const save = async () => {
    if (!name.trim()) return toast.error('Name is required');
    if (phone && !validPhone) return toast.error('Enter a valid 10-digit Indian mobile');
    if (phoneChanged && !otpRequired) {
      // send OTP first
      try {
        await authClient.post('/otp/send', { phone });
        toast.success(isPhoneMock ? 'OTP sent (use 123456 for testing)' : 'OTP sent successfully!');
        setOtpRequired(true);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'OTP failed');
      }
      return;
    }
    if (phoneChanged && otpRequired) {
      try {
        await authClient.post('/otp/verify', { phone, otp });
      } catch (e) {
        return toast.error(e.response?.data?.detail || 'Invalid OTP');
      }
    }
    setSaving(true);
    try {
      await client.put('/profile', {
        name: name.trim(),
        phone: phone || null,
        email_consent: emailConsent,
        whatsapp_consent: whatsappConsent,
      });
      toast.success('Profile saved');
      setOtpRequired(false); setOtp('');
      await refreshUser();
      const { data: p } = await authClient.get('/profile');
      setProfile(p);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addAddress = async (e) => {
    e.preventDefault();
    if (addrForm.address_line.trim().length < 10) return toast.error('Please enter a complete address');
    try {
      await authClient.post('/addresses', { ...addrForm });
      toast.success('Address added');
      const { data: a } = await authClient.get('/addresses');
      setAddresses(a);
      setAddrForm({ label: 'Home', address_line: '', phone: '', is_default: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteAddress = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    await authClient.delete(`/addresses/${id}`);
    setAddresses((prev) => prev.filter((a) => a.id !== id));
  };

  if (!user) {
    return (
      <div className="pb-24 md:pb-8 max-w-md mx-auto px-4 py-16 text-center">
        <User className="w-16 h-16 text-saffron mx-auto mb-4" />
        <h2 className="font-heading font-bold text-2xl text-textprimary mb-2">Sign in to view profile</h2>
        <p className="text-textsecondary font-body mb-6">Manage your saved details, phone and delivery addresses.</p>
        <button onClick={loginWithGoogle} data-testid="profile-signin-btn" className="inline-flex items-center gap-2 bg-saffron text-white px-6 py-3 rounded-2xl font-heading font-semibold shadow-warm">
          <LogIn className="w-4 h-4" /> Sign in with Google
        </button>
      </div>
    );
  }

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-saffron" /></div>;

  return (
    <div className="pb-32 md:pb-8 max-w-3xl mx-auto px-4 md:px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-heading font-extrabold text-3xl text-textprimary">My Profile</h1>
        <button
          onClick={logout}
          data-testid="profile-logout-btn"
          className="flex items-center gap-2 border border-maroon/20 hover:border-maroon/40 bg-white text-maroon hover:bg-maroon hover:text-white px-4 py-2.5 rounded-2xl font-heading font-bold text-sm transition-all shadow-sm active:scale-95 duration-200"
        >
          <LogOut className="w-4 h-4" /> Log Out
        </button>
      </div>

      <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige mb-4">
        <h2 className="font-heading font-bold text-lg text-maroon mb-4 flex items-center gap-2"><User className="w-4 h-4" /> Personal Info</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-textsecondary font-body">Name</label>
            <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-borderbeige rounded-xl font-body text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs text-textsecondary font-body flex items-center gap-1"><Mail className="w-3 h-3" /> Email (read only)</label>
            <input data-testid="profile-email" value={profile?.email || ''} readOnly className="w-full px-3 py-2 border border-borderbeige rounded-xl bg-bgsecondary text-textsecondary font-body text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs text-textsecondary font-body flex items-center gap-1"><Phone className="w-3 h-3" /> Mobile</label>
            <div className="flex gap-2 mt-1">
              <div className="flex items-center px-3 border border-borderbeige rounded-xl bg-bgsecondary text-textsecondary text-sm">+91</div>
              <input data-testid="profile-phone" value={phone} onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setOtpRequired(false); }} placeholder="10-digit mobile" inputMode="numeric" className="flex-1 px-3 py-2 border border-borderbeige rounded-xl font-body text-sm" />
            </div>
            {otpRequired && (
              <div className="mt-2 bg-golden/10 p-3 rounded-xl">
                <label className="text-xs text-maroon font-heading font-semibold">Verify new phone (OTP)</label>
                <input data-testid="profile-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={isPhoneMock ? "Enter OTP (123456)" : "Enter OTP"} className="w-full px-3 py-2 mt-1 border border-borderbeige rounded-xl font-mono text-center text-lg tracking-widest" maxLength={6} />
              </div>
            )}
          </div>
          <div className="space-y-2 pt-2">
            <label className="flex items-center gap-2 cursor-pointer"><input data-testid="profile-email-consent" type="checkbox" checked={emailConsent} onChange={(e) => setEmailConsent(e.target.checked)} className="accent-saffron" /><span className="text-xs font-body text-textsecondary">Email me offers &amp; updates</span></label>
            <label className="flex items-center gap-2 cursor-pointer"><input data-testid="profile-whatsapp-consent" type="checkbox" checked={whatsappConsent} onChange={(e) => setWhatsappConsent(e.target.checked)} className="accent-saffron" /><span className="text-xs font-body text-textsecondary">WhatsApp updates &amp; delivery tracking</span></label>
          </div>
          <button onClick={save} disabled={saving} data-testid="profile-save-btn" className="w-full bg-saffron text-white py-2.5 rounded-2xl font-heading font-bold shadow-warm hover:bg-saffron-dark disabled:opacity-60 flex items-center justify-center gap-2 mt-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : phoneChanged && !otpRequired ? <ShieldCheck className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : phoneChanged && !otpRequired ? 'Verify Phone & Save' : 'Save Changes'}
          </button>
        </div>
      </section>

      <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
        <h2 className="font-heading font-bold text-lg text-maroon mb-4 flex items-center gap-2"><MapPin className="w-4 h-4" /> Saved Addresses</h2>
        <div className="space-y-2 mb-4">
          {addresses.length === 0 && <p className="text-sm text-textsecondary font-body">No addresses yet. Add one below.</p>}
          {addresses.map((a) => (
            <div key={a.id} data-testid={`profile-address-${a.id}`} className="border border-borderbeige rounded-xl p-3 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-heading font-bold bg-maroon text-white px-2 py-0.5 rounded-full">{a.label}</span>
                  {a.is_default && <span className="text-[10px] font-heading font-semibold bg-golden/30 text-maroon px-2 py-0.5 rounded-full">DEFAULT</span>}
                </div>
                <p className="text-sm font-body text-textprimary">{a.address_line}</p>
                {a.phone && <p className="text-xs text-textsecondary font-body">📞 {a.phone}</p>}
              </div>
              <button onClick={() => deleteAddress(a.id)} className="text-xs text-maroon font-heading" data-testid={`profile-delete-addr-${a.id}`}>Delete</button>
            </div>
          ))}
        </div>
        <form onSubmit={addAddress} className="border-t border-borderbeige pt-4 space-y-2">
          <div className="flex gap-2">
            {['Home', 'Work', 'Other'].map((l) => (
              <button key={l} type="button" onClick={() => setAddrForm({ ...addrForm, label: l })} className={`text-xs px-3 py-1 rounded-full font-heading font-semibold ${addrForm.label === l ? 'bg-maroon text-white' : 'bg-white border border-borderbeige text-textsecondary'}`}>{l}</button>
            ))}
          </div>
          <textarea data-testid="profile-new-address" value={addrForm.address_line} onChange={(e) => setAddrForm({ ...addrForm, address_line: e.target.value })} rows={2} placeholder="Full address with pincode" className="w-full px-3 py-2 border border-borderbeige rounded-xl text-sm font-body" />
          <label className="flex items-center gap-2 text-xs font-body"><input type="checkbox" checked={addrForm.is_default} onChange={(e) => setAddrForm({ ...addrForm, is_default: e.target.checked })} className="accent-saffron" />Set as default</label>
          <button type="submit" data-testid="profile-add-address" className="w-full bg-maroon text-white py-2 rounded-2xl font-heading font-semibold text-sm">Add Address</button>
        </form>
      </section>
    </div>
  );
}
